import type { Permission } from "./permissions";

/**
 * The wire shapes, as the server's DTO modules emit them.
 *
 * Hand-mirrored rather than generated or imported. The server is ESM TypeScript
 * in the same repository, so importing `src/api/dto/*.ts` would compile — and
 * would also drag `drizzle-orm` and `node:crypto` types into a browser bundle
 * through the transitive `import type` chain. These are the ~10 shapes the panel
 * reads; the drift risk is small and the coupling avoided is not.
 *
 * Every `Wh` figure arrives as a JSON number that the server has already
 * range-checked against `Number.MAX_SAFE_INTEGER` (spec 10.2), so arithmetic on
 * it here is safe.
 */

/**
 * A role as an operator row names it. `grantsAll` is the built-in Administrator:
 * every permission, including any a later release adds.
 */
export interface RoleSummary {
  readonly id: string;
  readonly name: string;
  readonly grantsAll: boolean;
}

/**
 * Who is signed in, and what they may do.
 *
 * `permissions` is the effective set — roles' permissions plus direct ones for an
 * operator, the scope mapping for a service token — computed by the server. The
 * panel never derives access from role names; it asks this list.
 */
export interface Principal {
  readonly type: "user" | "service";
  readonly id: string;
  readonly name: string;
  readonly email: string | null;
  /** Empty for a service token, which has scopes in config rather than roles. */
  readonly roles: readonly RoleSummary[];
  readonly permissions: readonly Permission[];
}

/** `GET /roles`. A `system` role (only Administrator) cannot be edited or deleted. */
export interface Role {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly grantsAll: boolean;
  readonly system: boolean;
  /** For a `grantsAll` role, the full catalog. */
  readonly permissions: readonly Permission[];
  readonly memberCount: number;
  readonly createdAt: string;
}

/** `DELETE /roles/:id`. */
export interface DeletedRole {
  readonly deleted: true;
  /** How many operators held the role and lost it. */
  readonly removedFrom: number;
}

export interface Depot {
  readonly id: string;
  readonly name: string;
  readonly timezone: string;
  /** Who takes drivers' money here; `null` refuses public orders at this site. */
  readonly paymentProvider: { readonly id: string; readonly label: string } | null;
  /** Energy delivered at this site in the requested window; see `UsageTotals`. */
  readonly usage: UsageTotals | null;
}

/**
 * Energy delivered in a window (server spec §8.3), on a station row or a depot
 * row.
 *
 * Distinct from `Usage` further down, and the two must not be conflated: that one
 * is a *card* measured against its allowance in a period the policy defines, and
 * carries a quota and an in-flight estimate. This one is a *place* measured over
 * an interval an operator picked, and carries neither — there is no quota on a
 * charger, and an operator's total may not contain an estimate.
 *
 * `null` on the row — not a zeroed `Usage` — means no window was requested. The
 * distinction is load-bearing in both directions: a zero here is a station that
 * genuinely charged nothing, and rendering the two the same way would report a
 * working charger as idle.
 *
 * `from`/`to` are echoed by the server so a figure can be labelled with the
 * interval it actually covers rather than with whatever the picker says now.
 */
export interface UsageTotals {
  readonly from: string;
  readonly to: string;
  /** Closed sessions only, keyed on `attributed_at`. A live session is not in here. */
  readonly energyWh: number;
  /**
   * How many sessions the figure is made of. Zero energy over forty sessions is a
   * fault; zero energy over zero sessions is a quiet week.
   */
  readonly closedSessionCount: number;
}

/**
 * One gun. `evseId` is the bay it belongs to; on a 1.6 station every EVSE has
 * one gun, numbered 1 (docs/ocpp-201-spec.md D2). `lib/evse.ts` names them.
 */
export interface Connector {
  readonly evseId: number;
  readonly connectorId: number;
  /** The plug, as a 2.0.1 station reports it (`cType2`, `cCCS2`…); null when unsaid. */
  readonly connectorType: string | null;
  readonly status: string;
  readonly errorCode: string;
  readonly info: string | null;
  readonly vendorErrorCode: string | null;
  readonly statusUpdatedAt: string;
}

/**
 * One configuration key from spec 4.4's boot-time push, as the server recorded
 * it. `state` is the verdict; the desired/reported pair is the evidence, and
 * showing the verdict without the pair leaves an operator with nothing to act on.
 */
export interface ConfigKeyReport {
  readonly key: string;
  readonly desired: string;
  readonly reported: string | null;
  readonly writeStatus: string;
  readonly state:
    | "applied"
    | "ignored"
    | "readonly"
    | "rejected"
    | "unsupported"
    | "reboot-required"
    | "unverified";
}

export interface ChargePoint {
  readonly id: string;
  readonly depotId: string;
  readonly depotName: string;
  readonly depotTimezone: string;
  readonly registrationStatus: string;
  readonly vendor: string | null;
  readonly model: string | null;
  readonly serial: string | null;
  readonly firmwareVersion: string | null;
  /**
   * The OCPP subprotocol agreed at the last successful handshake, verbatim
   * (`ocpp1.6`). `null` if it has not connected since the server recorded it.
   */
  readonly ocppVersion: string | null;
  /**
   * What the station offered when it was last refused for want of a common
   * version; `null` when it is not being refused. See `lib/protocol.ts`.
   */
  readonly refusedSubprotocols: readonly string[] | null;
  readonly subprotocolRefusedAt: string | null;
  readonly heartbeatIntervalS: number;
  readonly effectiveMeterIntervalS: number | null;
  readonly configDriftDetected: boolean;
  /**
   * `null` for a station that has not booted since this check existed — which is
   * not the same as a station with nothing wrong, so the view says which it is.
   */
  readonly configReport: readonly ConfigKeyReport[] | null;
  readonly configCheckedAt: string | null;
  readonly lastBootAt: string | null;
  /** Why it last booted, as a 2.0.1 station states it (`PowerUp`…); null for 1.6. */
  readonly lastBootReason: string | null;
  readonly lastHeartbeatAt: string | null;
  /** Live socket state, not a stored column. See the server's `toChargePointDto`. */
  readonly connected: boolean;
  /**
   * pay-ui spec §4.2 — whether a driver scanning this station's sticker is quoted a
   * price or gets a 404.
   */
  readonly publicAccess: boolean;
  /**
   * pay-ui spec §7.1 — what a driver sees this station called, or `null` when
   * nobody has named it and the driver sees its id instead.
   */
  readonly displayLabel: string | null;
  /**
   * The price it sells at, kept even when it is off sale so that putting it back is
   * a switch rather than a decision. `null` for a station never priced.
   */
  readonly tariff: Tariff | null;
  /** When an operator hid (retired) it, or `null` for a station in service. */
  readonly hiddenAt: string | null;
  readonly connectors: readonly Connector[];
  /** Energy delivered at this station in the requested window; see `UsageTotals`. */
  readonly usage: UsageTotals | null;
}

/**
 * A price. pay-ui spec §6.1, §11.1.
 *
 * **Every money field is a decimal string of minor units** — `"45"` is 45 cents.
 * Not a `number`, because these are `bigint` on the server and rounding a price on
 * the way to a screen is not a trade worth making. `lib/tariff.ts` turns them into
 * something an operator reads.
 */
export interface Tariff {
  readonly id: string;
  readonly name: string;
  readonly currency: string;
  readonly priceMinorPerKwh: string;
  readonly minAmountMinor: string;
  readonly maxAmountMinor: string;
  readonly presetAmountsMinor: readonly string[];
  /** A retired price. Served rather than hidden, so a station selling on one can be named. */
  readonly active: boolean;
}

/**
 * A price *version*, which is what `GET /tariffs` and `PUT /tariffs/:id` deal in.
 *
 * The server never edits a price's money columns: changing one mints a successor on
 * the same lineage, retires the predecessor, and moves every subscribed station onto
 * the new version. So a version is what a station and an order point at, and the
 * lineage is the price — every screen that talks about "the price" is talking about a
 * set of these rows, which `lib/priceLineage.ts` folds back together.
 *
 * A station's embedded tariff stays the narrower `Tariff`: the fleet table answers
 * "what does this station charge", and a version's provenance is noise in that
 * sentence.
 */
export interface TariffVersion extends Tariff {
  readonly lineageId: string;
  /** When this version came into force. ISO-8601. */
  readonly createdAt: string;
}

/** A version as the price list serves it, with the stations currently pointing at it. */
export interface TariffListing extends TariffVersion {
  readonly subscriberCount: number;
}

/**
 * What `PUT /tariffs/:id` reports back.
 *
 * `rotated` is the server saying whether the change was commercial: a changed figure
 * mints a version and repoints stations, a corrected label does not. The count is the
 * blast radius — this is the one operator action that writes rows the operator did not
 * name, and the panel says how many.
 */
export interface PriceChange {
  readonly tariff: TariffVersion;
  readonly rotated: boolean;
  readonly supersededId: string | null;
  readonly repointedChargePoints: number;
}

export interface OcppMessage {
  readonly id: string;
  readonly chargePointId: string;
  readonly direction: "inbound" | "outbound";
  /** 2 Call, 3 CallResult, 4 CallError (spec 3.1). */
  readonly messageType: number;
  readonly action: string | null;
  readonly messageId: string;
  readonly payload: unknown;
  readonly occurredAt: string;
}

/**
 * What provisioning and rotation return. The password is on the wire exactly
 * once — the server keeps only an argon2id hash — and `passwordShownOnce` is the
 * server saying so rather than this client assuming it.
 */
export interface ProvisionedStation {
  readonly chargePoint: ChargePoint;
  readonly password: string;
  readonly passwordShownOnce: boolean;
}

/**
 * The two public addresses the serving deployment declares — `GET /deployment`.
 *
 * `null` means "this deployment has not been told", which is the normal state of a
 * dev checkout and is deliberately distinguishable from a missing field. See
 * `lib/deployment.ts`.
 */
export interface Deployment {
  /** `wss://host/ocpp`, with a station id still to be appended. */
  readonly ocppBaseUrl: string | null;
  /** The pay-ui origin a driver's phone reaches, no trailing slash. */
  readonly payUiBaseUrl: string | null;
}

export interface ChargePointDetail extends ChargePoint {
  readonly recentMessages: readonly OcppMessage[];
  /** The closed set the server will originate; anything else is refused (spec 4.2). */
  readonly availableCommands: readonly string[];
  /** The version `availableCommands` was built for: the live connection's, else the stored one. */
  readonly commandVersion: string;
  /** Every action a logged frame can carry, for the frame filter. */
  readonly frameActions: readonly string[];
  /** Whether a session or public order names it: decides hide (true) or delete (false). */
  readonly hasHistory: boolean;
}

/** Spec 3.6's three states, as `charging_session_state_check` permits them. */
export type SessionState = "active" | "ended" | "abandoned";

/** Spec 6.5's flags. Unknown strings are rendered rather than dropped. */
export type AnomalyFlag =
  | "meter_regression"
  | "clock_suspect"
  | "orphaned_session"
  | "late_stop"
  | "authorization_refused"
  | "missed_start";

export interface PowerCurvePoint {
  /** ISO-8601. Short keys because a session carries up to 20 of them. */
  readonly t: string;
  readonly w: number;
}

export interface Session {
  readonly id: string;
  /**
   * What the station itself calls this session (spec 3.5). Text: a 2.0.1 station
   * mints its own (docs/ocpp-201-spec.md D1); a 1.6 one is still a number.
   */
  readonly ocppTransactionId: string;
  readonly chargePointId: string;
  readonly depotId: string;
  readonly depotName: string;
  readonly depotTimezone: string;
  /** The EVSE that charged (docs/ocpp-201-spec.md D2). */
  readonly evseId: number;
  /** The gun used, within that EVSE. Always 1 on a 1.6 station. */
  readonly connectorId: number;
  readonly idTag: string;
  readonly identityId: string | null;
  /** `null` for a card nobody has named yet. The tag is still shown. */
  readonly identityLabel: string | null;
  readonly state: SessionState;
  readonly startedAtCp: string;
  readonly endedAtCp: string | null;
  readonly meterStartWh: number;
  readonly meterStopWh: number | null;
  /** Committed and billable. `null` until the session closes — spec 6.5. */
  readonly energyWh: number | null;
  /**
   * An estimate from interim readings, for an open session only (spec 6.3). Never
   * the billable figure, which is why the panel never adds the two together or
   * falls back from one to the other.
   */
  readonly inFlightWh: number | null;
  readonly stopReason: string | null;
  readonly attributedAt: string | null;
  readonly anomalyFlags: readonly string[];
  readonly peakPowerW: number | null;
  readonly avgPowerW: number | null;
}

/** What a push attempt came to; mirrors `SESSION_PUSH_OUTCOMES` on the server. */
export const SESSION_PUSH_OUTCOMES = [
  "saved",
  "already_saved",
  "ignored",
  "rejected",
  "unauthorized",
  "failed",
  "skipped",
  "turned_off",
] as const;
export type SessionPushOutcome = (typeof SESSION_PUSH_OUTCOMES)[number];

/** Where a closed session was pushed (docs/session-push-plan.md §6). */
export interface SessionPush {
  readonly app: { readonly id: string; readonly name: string };
  readonly state: "pending" | "done" | "parked";
  readonly attempts: number;
  readonly lastOutcome: SessionPushOutcome | null;
  readonly lastMessage: string | null;
  /** The partner's reference for what it saved. */
  readonly recordNo: string | null;
  readonly lastAttemptAt: string | null;
  /** Only while pending. */
  readonly nextAttemptAt: string | null;
}

/** `GET /session-pushes` — one request a push made. Neither body; open it for those. */
export interface SessionPushAttempt {
  readonly id: string;
  readonly attemptedAt: string;
  readonly session: { readonly id: string; readonly chargePointId: string };
  readonly app: { readonly id: string; readonly name: string };
  /** What was sent as `plate_num`. */
  readonly plate: string | null;
  /** Null: no answer at all. */
  readonly httpStatus: number | null;
  readonly outcome: SessionPushOutcome;
  /** Why there was no answer, in the server's words. */
  readonly error: string | null;
  readonly durationMs: number;
}

/** `GET /session-pushes/:id` — the request as sent and the answer as read. Never the token. */
export interface SessionPushAttemptDetail extends SessionPushAttempt {
  readonly url: string;
  readonly requestBody: unknown;
  readonly responseBody: string | null;
}

export interface SessionDetail extends Session {
  /** Empty when no app pushes this session. */
  readonly pushes: readonly SessionPush[];
  /** Apps that push this session but were never sent it: it closed before their push was set. */
  readonly unsent: readonly { readonly id: string; readonly name: string }[];
  readonly startedAtServer: string;
  readonly endedAtServer: string | null;
  readonly consolidatedAt: string | null;
  readonly sampleCount: number | null;
  readonly socStartPct: number | null;
  readonly socEndPct: number | null;
  readonly curve: readonly PowerCurvePoint[] | null;
  /**
   * `live` is built from samples that have not been summarised yet and will move
   * on the next MeterValues; `consolidated` is final and outlives the samples.
   * The chart says which, because a half-drawn session must not read as a
   * finished one.
   */
  readonly curveSource: "consolidated" | "live" | null;
  readonly signedMeterData: unknown;
  readonly retentionHoldUntil: string | null;
}

export type PeriodType = "calendar_month" | "calendar_week" | "rolling_30d";

export interface Policy {
  readonly id: string;
  readonly name: string;
  readonly periodType: PeriodType;
  readonly quotaWh: number;
  readonly quotaKwh: number;
  readonly warnThresholds: readonly number[];
  readonly allowConcurrentTx: boolean;
  readonly createdAt: string;
  readonly assignedHolderCount: number;
}

/** The policy as it appears *on a holder* — carries the assignment date. */
export interface AssignedPolicy {
  readonly id: string;
  readonly name: string;
  readonly periodType: PeriodType;
  readonly quotaWh: number;
  readonly warnThresholds: readonly number[];
  readonly allowConcurrentTx: boolean;
  readonly effectiveFrom: string;
}

/**
 * The policy that governs a holder or a card, and the holder it is assigned to.
 * A policy is pooled over its owner's subtree (docs/identity-holders-plan.md §4),
 * so the owner is often an ancestor rather than the holder being looked at.
 */
export interface GoverningAllowance {
  readonly holder: { readonly id: string; readonly name: string };
  readonly policy: AssignedPolicy;
}

export type HolderType = "person" | "group";

/** Who an identity belongs to. Holders nest; Express is the system group. */
export interface HolderRef {
  readonly id: string;
  readonly name: string;
  readonly type: HolderType;
}

export interface Holder {
  readonly id: string;
  readonly name: string;
  readonly type: HolderType;
  /** `null` at the top level. */
  readonly parentHolderId: string | null;
  /** True only for Express, which can be renamed but not moved, retyped or deleted. */
  readonly isSystem: boolean;
  readonly externalRef: string | null;
  /** `holder_status_check`. Nothing sets `disabled` yet. */
  readonly status: "active" | "disabled";
  readonly createdAt: string;
}

export interface HolderListItem extends Holder {
  /** Cards held directly, not through a subgroup. */
  readonly identityCount: number;
  readonly childCount: number;
  readonly allowance: GoverningAllowance | null;
}

/**
 * A pool's figure for the period containing now. Every member of the pool shows
 * the same one, which is the point: the quota is shared.
 */
export interface PoolPeriod {
  readonly label: string;
  /** Whose pool — the holder the governing policy is assigned to. */
  readonly holder: { readonly id: string; readonly name: string };
  readonly periodStart: string;
  readonly periodEnd: string;
  /** The figure a chargeback export would use. */
  readonly committedWh: number;
  /**
   * A live estimate from open sessions, kept separate from `committedWh` on
   * purpose (spec 6.3) — it must never be mistaken for the billable figure.
   */
  readonly inFlightWh: number;
  readonly quotaWh: number;
  readonly closedSessionCount: number;
  readonly consumedPct: number | null;
}

export interface HolderDetail extends Holder {
  readonly allowance: GoverningAllowance | null;
  readonly currentPeriod: PoolPeriod | null;
}

/** An RFID card, or a vehicle that authenticates with its VIN. */
export type TokenType = "rfid" | "vin";

/** `Accepted` or `Blocked` — the only two values the schema's CHECK permits. */
export type IdentityStatus = "Accepted" | "Blocked";

export interface Identity {
  readonly id: string;
  readonly idTag: string;
  readonly tokenType: TokenType;
  /** Not nullable: `identity.label` is `NOT NULL`. */
  readonly label: string;
  /** A partner's name for it; what a session push sends as the plate, before `label`. */
  readonly externalRef: string | null;
  readonly status: IdentityStatus;
  readonly expiryDate: string | null;
  readonly holder: HolderRef;
  readonly createdAt: string;
  /** Inherited from the holder's tree; `null` when nothing governs it. */
  readonly allowance: GoverningAllowance | null;
}

export interface IdentityDetail extends Identity {
  readonly currentPeriod: PoolPeriod | null;
  /**
   * Every session ever attributed to this card, closed or still running — not the
   * period-scoped `currentPeriod.closedSessionCount`.
   *
   * It is the one fact that decides whether the card can be deleted, and it comes
   * from the server rather than being inferred here so the button an operator sees
   * and the answer `DELETE` gives cannot disagree.
   */
  readonly sessionCount: number;
}

export interface CommandResult {
  readonly command: string;
  readonly response: unknown;
}

/** Spec 6.3's trigger table. Every value is one of these three. */
export type AllowanceEventType = "threshold_crossed" | "quota_exceeded" | "overage_extended";

/**
 * Derived from `type` by the server, never stored (spec 6.3). Present on the DTO
 * so the panel does not restate the mapping — a third copy of it could disagree
 * with the two that already exist.
 */
export type AllowanceSeverity = "info" | "warning";

export interface AllowanceEvent {
  readonly id: string;
  readonly type: AllowanceEventType;
  readonly severity: AllowanceSeverity;
  /** Percent of quota that triggered it. */
  readonly thresholdPct: number;
  readonly consumedWh: number;
  readonly quotaWh: number;
  /**
   * What was true when it fired, which is not the threshold: an `overage_extended`
   * at 110 may have fired on a close that reached 137%.
   */
  readonly consumedPct: number | null;
  /** The pool the threshold was crossed in — the governing policy's owner. */
  readonly holderId: string;
  readonly holderName: string;
  /**
   * The card whose session closed the gap. `null` once that session has been
   * swept (spec 12), since the event reaches the card only through it.
   */
  readonly identityId: string | null;
  readonly identityLabel: string | null;
  readonly idTag: string | null;
  /** `null` once the triggering session has been swept (spec 12). */
  readonly sessionId: string | null;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly createdAt: string;
}

/**
 * The sixteen states a public order can hold. pay-ui spec §8.
 *
 * Spelled out rather than widened to `string` so a `Record` keyed on this fails to
 * compile when the server grows a thirteenth — which is the only way a panel finds
 * out, given the panel is an HTTP client and imports no server code.
 * `orderStateTone` still tolerates an unknown value at runtime for the window
 * between a server deploy and this bundle's.
 *
 * **In the server's own order**, matching `PUBLIC_ORDER_STATES` in
 * `src/db/schema/publicCharging.ts` name for name. That is not tidiness: this list
 * once carried `cancelled` and `refunding`, which the server has never emitted, and
 * omitted `expired_unplugged` and `start_failed`, which it emits on every hold that
 * lapses after payment. `test/publicOrder.test.ts` now reads the server's array and
 * fails when the two disagree.
 */
export const PUBLIC_ORDER_STATES = [
  "pending",
  "expired",
  "starting",
  "awaiting_plug",
  "expired_unplugged",
  "start_failed",
  "charging",
  "stopping",
  "completed",
  "refund_required",
  "refunded",
  "refund_failed",
  /**
   * The `pre_auth` tail, added when §11.3's hold-then-capture flow shipped, and
   * **none of it reuses a refund state**: a capture moves money in, a refund moves
   * it out, and a state meaning either would make every revenue question ambiguous.
   *
   * The panel was missing all four for a release. That is not cosmetic — a
   * `Record` keyed on this union decides a row's colour and its filter label, so a
   * captured order fell through to the `idle` tone and could not be filtered for at
   * all. `test/publicOrder.test.ts` reads the server's array and is what caught it.
   */
  "capture_required",
  "captured",
  "released",
  "capture_failed",
] as const;

export type PublicOrderState = (typeof PUBLIC_ORDER_STATES)[number];

/**
 * One public charge order, as an operator sees it. pay-ui spec §9.5.
 *
 * **Every money field is a decimal string of minor units**, as `Tariff` is, and for
 * the same reason. `lib/publicOrder.ts` turns them into sentences.
 *
 * What is deliberately absent is the driver's order secret: §5 makes the driver's
 * credential unreachable from the operator hierarchy, and `dto/publicOrderView.ts`
 * never puts it on the wire. If it is missing from this interface it cannot be
 * rendered by accident.
 */
export interface PublicOrder {
  readonly id: string;
  readonly chargePointId: string;
  /** The bay the order held (docs/ocpp-201-spec.md D2). */
  readonly evseId: number;
  readonly depotName: string;
  readonly depotTimezone: string;
  readonly tariffId: string;
  readonly tariffName: string;
  readonly state: PublicOrderState;
  /**
   * Which of §11.3's two flows this order was created under, snapshotted at
   * creation. Read it before rendering any money figure — `settledAt` is set for a
   * hold exactly as it is for a purchase, so it cannot tell a debit from an
   * authorisation on its own.
   */
  readonly paymentFlow: PaymentFlow;
  readonly currency: string;
  readonly priceMinorPerKwh: string;
  readonly amountMinor: string;
  readonly budgetWh: string;
  /** The synthetic tag the CSMS minted for this order (§4.1). */
  readonly idTag: string;
  readonly sessionId: string | null;
  /** `null` until the session closes and commits a figure. */
  readonly deliveredWh: string | null;
  /** What the close decided was left over. `null` before there is a close. */
  readonly unusedMinor: string | null;
  readonly refundedMinor: string;
  /**
   * Derived on the server, and not the same as `unusedMinor`: §11.3's floor leaves
   * a `completed` order holding unused money that is deliberately not owed back.
   * Only the refund states carry a debt here.
   */
  readonly refundOwedMinor: string;
  readonly refundReason: string | null;
  readonly stopCause: string | null;
  readonly holdExpiresAt: string;
  readonly settledAt: string | null;
  readonly startedAt: string | null;
  readonly endedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** The gateway's reference for the payment that settled it, for the portal. */
  readonly settlementRef: string | null;
  readonly paymentCount: number;
}

/**
 * One `payment_transaction` row. pay-ui spec §9.2, §9.4.
 *
 * An order has several of these when a settlement was retried, so they are a list
 * on the detail page rather than a summary: "which attempt actually took the money"
 * is the question this table exists to answer.
 */
export interface PublicPayment {
  readonly id: string;
  readonly provider: string;
  readonly providerRef: string;
  readonly status: string;
  readonly amountMinor: string;
  readonly currency: string;
  readonly approvalCode: string | null;
  readonly paymentOption: string | null;
  readonly occurredAt: string;
  /** §9.4's reconciler state, `null` for an attempt it never took an interest in. */
  readonly settleState: string | null;
  readonly settleAttempts: number;
  readonly settleNextAt: string | null;
}

/** The charging session an order produced, if it got that far. */
export interface PublicOrderSession {
  readonly id: string;
  readonly ocppTransactionId: string;
  readonly state: string;
  /** `null` while open: energy is committed at close, and zero would be a claim. */
  readonly energyWh: string | null;
  readonly startedAtCp: string;
  readonly endedAtCp: string | null;
  readonly stopReason: string | null;
}

export interface PublicOrderDetail extends PublicOrder {
  readonly payments: readonly PublicPayment[];
  readonly session: PublicOrderSession | null;
}

/**
 * The refund queue as one figure. pay-ui spec §9.5.
 *
 * Its own endpoint rather than a field on the list, because the answer must not
 * depend on which page is open — computing it from twenty visible rows would make
 * a fact about the queue into a fact about pagination.
 */
export interface RefundQueueSummary {
  readonly owedOrders: number;
  readonly owedMinor: string;
  /** When the oldest outstanding debt was incurred. §9.5 alerts on this, not size. */
  readonly oldestOwedAt: string | null;
}

/**
 * A condition the server could not resolve on its own. `system_alert`.
 *
 * Deliberately not merged with `AllowanceEvent`, which it superficially
 * resembles. An allowance event is an immutable fact about a fleet identity's
 * quota; an alert is a *state* with a lifecycle — raised by the system,
 * acknowledged by a person, resolved when the condition is gone — and the two
 * screens ask different questions of their rows.
 */
export type AlertSeverity = "warning" | "error" | "critical";

export interface SystemAlert {
  readonly id: string;
  /** The catalogue's dotted code — `refund.rail_state_unknown`, `assert.<name>`. */
  readonly code: string;
  readonly severity: AlertSeverity;
  readonly summary: string;
  /** `order`, `charge_point`, … and the id, when the alert is about one thing. */
  readonly subjectType: string | null;
  readonly subjectId: string | null;
  /** The raiser's own context object, verbatim. Figures arrive as strings. */
  readonly details: Readonly<Record<string, unknown>> | null;
  /**
   * How many times the condition has been seen since it was first raised. The
   * figure that turns a condition into a duration: 2 is a blip, 2,880 is a day of
   * a worker getting nowhere.
   */
  readonly occurrences: number;
  readonly firstSeenAt: string;
  readonly lastSeenAt: string;
  readonly acknowledgedAt: string | null;
  readonly acknowledgedBy: string | null;
  readonly resolvedAt: string | null;
  /** `null` on a resolved alert means the system cleared its own condition. */
  readonly resolvedBy: string | null;
  readonly resolutionNote: string | null;
}

/** Open alerts by severity, for the rail's badge. Every level present, zeros included. */
export interface AlertSummary {
  readonly open: Readonly<Record<AlertSeverity, number>>;
}

/**
 * How a public charge takes a driver's money — pay-ui spec §11.3.
 *
 * Mirrors the server's `PAYMENT_FLOWS`. Two words, and the difference between them
 * is whether a stranger's card is debited or only authorised, so they are never
 * treated as interchangeable labels for the same thing.
 */
export type PaymentFlow = "purchase" | "pre_auth";

/**
 * `GET /settings/payment-flow` — the switch, and enough context to render it
 * honestly.
 *
 * Four separate facts rather than one value, because "purchase" means two different
 * things to an operator depending on how it got there: a decision somebody made, or
 * the default this server was started with and nobody has revisited.
 */
export interface PaymentFlowSetting {
  /** What a new order would be created under right now. */
  readonly flow: PaymentFlow;
  /** Whether `flow` is stored in the database or is falling through to `configured`. */
  readonly source: "configured" | "stored";
  /** `PUBLIC_PAYMENT_FLOW`, as this process was started with. */
  readonly configured: PaymentFlow;
  /**
   * Whether the configured rail can hold money at all. `false` makes `pre_auth`
   * unofferable — the server answers 409 for it — so the UI must not present it as
   * a choice that merely failed to save.
   */
  readonly preAuthAvailable: boolean;
  /** When the stored value was last written, or `null` while nothing is stored. */
  readonly updatedAt: string | null;
}

/**
 * One customer account, as the operator's panel reads it. pay-ui spec §20.7.
 *
 * **There is no `authSubject` here and there must never be.** It is Google's
 * opaque identifier for a person and the key the account is looked up by; the
 * server does not send it, and a field for it would be an invitation to start.
 *
 * **An account is an index, not a wallet.** §1.0.1 — this system stores no value.
 * Nothing on this row is a balance, and `settledMinor` is a sum over orders
 * computed per request, not a number anybody can spend.
 */
export interface Customer {
  readonly id: string;
  readonly authProvider: string;
  readonly email: string;
  readonly displayName: string | null;
  readonly status: string;
  readonly lastLoginAt: string | null;
  readonly createdAt: string;
  /** Every order this account owns, in any state. */
  readonly orderCount: number;
  /** How many of them took money. */
  readonly settledCount: number;
  /**
   * The sum of those orders' amounts, in minor units — or `null` when they span
   * more than one currency and no single figure is true. There is no exchange
   * rate in this system, so a mixed history has no total.
   */
  readonly settledMinor: string | null;
  /** The currency `settledMinor` is in, or `null` when there is no single one. */
  readonly settledCurrency: string | null;
  readonly lastOrderAt: string | null;
  /** Devices signed in right now: sessions neither expired nor revoked. */
  readonly liveSessions: number;
}

/**
 * One entry in spec 9's trail, as `GET /audit` returns it.
 *
 * `beforeState` and `afterState` are `unknown` and stay that way: they are
 * whatever the route recorded, their shape differs per action, and a type that
 * claimed to know it would be wrong for every entry written before it was added.
 * `lib/audit.ts` reads them without asserting anything about them.
 */
export interface AuditEntry {
  readonly id: string;
  readonly actorType: "user" | "service";
  /** A `admin_user` id, a service token's name, or `unknown` for a refused login. */
  readonly actorId: string;
  readonly actorLabel: string;
  /** Dotted and past tense: `depot.created`, `command.Reset`, `auth.login`. */
  readonly action: string;
  readonly targetType: string;
  readonly targetId: string;
  readonly beforeState: unknown;
  readonly afterState: unknown;
  readonly outcome: AuditOutcome;
  readonly actorIp: string | null;
  readonly occurredAt: string;
}

export const AUDIT_OUTCOMES = ["ok", "denied", "failed"] as const;

export type AuditOutcome = (typeof AUDIT_OUTCOMES)[number];

/**
 * One handshake at `/ocpp/{id}`, as `GET /connection-attempts` returns it. Kept by
 * the server for a day; see `src/ocpp/transport/attempts.ts` for each field.
 */
export interface ConnectionAttempt {
  readonly id: string;
  readonly attemptedAt: string;
  /** Without its query string. */
  readonly path: string;
  /** `null` when the path names nothing a station could be registered as. */
  readonly chargePointId: string | null;
  readonly remoteAddress: string | null;
  /** The header as sent, which a proxy may have written or a client may have forged. */
  readonly forwardedFor: string | null;
  readonly userAgent: string | null;
  readonly offeredSubprotocols: readonly string[];
  /** The shape of the credential: `absent`, `parsed`, `unsupported_scheme`, … */
  readonly authPresented: string;
  readonly outcome: ConnectionAttemptOutcome;
  readonly reason: string | null;
  /** `null` when the station hung up before it was sent anything. */
  readonly httpStatus: number | null;
  readonly ocppVersion: string | null;
  readonly closedAt: string | null;
  readonly closeCode: number | null;
  readonly closeReason: string | null;
}

export const CONNECTION_ATTEMPT_OUTCOMES = [
  "accepted",
  "unsupported_protocol",
  "rejected",
] as const;

export type ConnectionAttemptOutcome = (typeof CONNECTION_ATTEMPT_OUTCOMES)[number];

/**
 * A person who can sign in to this panel — not a driver, and not a fleet card.
 *
 * `invited` is an account that exists but has never had a password: it was created
 * from this panel and is waiting for its owner to open the link they were sent.
 * It cannot sign in, and it is not counted as an admin who could.
 */
export type OperatorStatus = "invited" | "active" | "disabled";

/** Which of the two things a sign-in link does: set a first password, or replace one. */
export type SignInLinkPurpose = "invite" | "reset";

export interface Operator {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly status: OperatorStatus;
  /** Sorted by name. */
  readonly roles: readonly RoleSummary[];
  /** Granted directly, outside any role. */
  readonly permissions: readonly Permission[];
  /** What the operator can actually do: their roles' permissions plus the direct ones. */
  readonly effectivePermissions: readonly Permission[];
  readonly lastLoginAt: string | null;
  readonly createdAt: string;
  /**
   * The link that is still usable, if one is. Its expiry only — the token itself is
   * shown once, at the moment it is issued, and the server keeps no copy it could
   * send again.
   */
  readonly pendingLink: {
    readonly purpose: SignInLinkPurpose;
    readonly expiresAt: string;
  } | null;
}

/** A freshly minted link's secret. The only response that ever carries it. */
export interface IssuedLink {
  readonly purpose: SignInLinkPurpose;
  readonly token: string;
  readonly expiresAt: string;
}

/** `POST /operators` — the account, and the invitation that lets its owner in. */
export interface InvitedOperator {
  readonly operator: Operator;
  readonly link: IssuedLink;
}

/** What the set-password page learns about a link before the password is chosen. */
export interface SignInLinkDetails {
  readonly purpose: SignInLinkPurpose;
  readonly email: string;
  readonly name: string;
}

/** `POST /auth/password` — how many other sessions the change signed out. */
export interface PasswordChange {
  readonly revokedSessions: number;
}

export const APP_LOG_LEVELS = [
  "trace",
  "debug",
  "info",
  "warn",
  "error",
  "fatal",
  "unknown",
] as const;
/** pino's levels, plus `unknown` for a line pino did not write (a stack trace, stderr). */
export type AppLogLevel = (typeof APP_LOG_LEVELS)[number];

/** One line of the server's log, as `GET /logs` returns it. */
export interface AppLogEntry {
  readonly id: string;
  readonly ts: string;
  readonly level: AppLogLevel;
  readonly msg: string;
  readonly cpId: string | null;
  /** Everything else on the line, as pino wrote it. */
  readonly data: Readonly<Record<string, unknown>>;
  /** From the one-off import of Docker's log, not the live sink. */
  readonly imported: boolean;
}

/** `GET /logs` — a page, newest first, and the cursor for the next, older one. */
export interface AppLogPage {
  readonly rows: readonly AppLogEntry[];
  readonly next: string | null;
}

/**
 * `GET /stats/*` — the dashboard's three aggregates. Three because each sits
 * behind the permission its data already needs (`routes/stats.ts`), so a
 * principal may hold any subset of them.
 */

export interface UptimeWindow {
  readonly from: string;
  readonly to: string;
  /** 0–1. Connected time over the time accepted stations were expected online. */
  readonly ratio: number;
  readonly connectedSeconds: number;
  readonly expectedSeconds: number;
}

/** A fleet table row: a station plus its last seven days of uptime; `null` before any history or when not in service. */
export interface ChargePointRow extends ChargePoint {
  readonly uptime: UptimeWindow | null;
}

/** One day of a station's uptime, in its depot's zone. */
export interface UptimeDay {
  /** `YYYY-MM-DD`, already in the depot's zone. */
  readonly date: string;
  readonly connectedSeconds: number;
  readonly expectedSeconds: number;
  /** `null` when nothing was expected: before the history began or the station existed. */
  readonly ratio: number | null;
  /** Connections the station itself ended that day: not a reconnect, not a server restart. */
  readonly disconnects: number;
}

/** `GET /charge-points/:id/uptime`. */
export interface StationUptime {
  readonly timeZone: string;
  readonly collectingSince: string | null;
  readonly last24h: UptimeWindow | null;
  readonly last7d: UptimeWindow | null;
  readonly last30d: UptimeWindow | null;
  /** Thirty days, oldest first, ending with today. */
  readonly daily: readonly UptimeDay[];
}

export interface FaultedConnectorStat {
  readonly chargePointId: string;
  readonly evseId: number;
  readonly connectorId: number;
  readonly status: string;
  readonly errorCode: string;
  readonly vendorErrorCode: string | null;
  readonly info: string | null;
  /** When the connector entered its current status. */
  readonly since: string;
}

/** `GET /stats/fleet` — now, rather than windowed. */
export interface FleetStats {
  readonly stations: {
    readonly total: number;
    readonly accepted: number;
    readonly pending: number;
    readonly rejected: number;
    readonly online: number;
  };
  /** Accepted stations without a live socket, longest-silent first. Capped; see the total. */
  readonly offline: readonly {
    readonly chargePointId: string;
    readonly depotId: string;
    readonly lastSeenAt: string | null;
  }[];
  readonly offlineTotal: number;
  readonly connectors: {
    readonly total: number;
    readonly byStatus: Readonly<Record<string, number>>;
  };
  /** Capped like `offline`; `faultedTotal` is the whole count. */
  readonly faulted: readonly FaultedConnectorStat[];
  readonly faultedTotal: number;
  readonly configDrift: { readonly total: number; readonly chargePointIds: readonly string[] };
  readonly rebootsLast24h: number;
  readonly uptime: {
    /** The first connection on record: before it there is no history, not downtime. */
    readonly collectingSince: string | null;
    readonly last24h: UptimeWindow | null;
    readonly last7d: UptimeWindow | null;
  };
}

/** `GET /stats/usage` — closed sessions attributed to the window. */
export interface UsageStats {
  readonly window: { readonly from: string; readonly to: string };
  readonly timeZone: string;
  readonly totals: {
    readonly energyWh: number;
    readonly sessions: number;
    readonly fleetEnergyWh: number;
    readonly fleetSessions: number;
    readonly publicEnergyWh: number;
    readonly publicSessions: number;
    readonly avgDurationS: number | null;
    readonly avgEnergyWh: number | null;
  };
  readonly activeSessions: number;
  readonly daily: readonly {
    readonly date: string;
    readonly fleetEnergyWh: number;
    readonly publicEnergyWh: number;
    readonly fleetSessions: number;
    readonly publicSessions: number;
  }[];
  readonly topStations: readonly {
    readonly chargePointId: string;
    readonly energyWh: number;
    readonly sessions: number;
  }[];
  /** 7 × 24 session starts: Monday first, local hours. */
  readonly heatmap: readonly (readonly number[])[];
  readonly stopReasons: readonly { readonly reason: string | null; readonly count: number }[];
}

/** `GET /stats/public` — orders placed in the window. Money is per currency, never summed. */
export interface PublicStats {
  readonly window: { readonly from: string; readonly to: string };
  readonly timeZone: string;
  readonly revenue: readonly {
    readonly currency: string;
    readonly orders: number;
    readonly grossMinor: string;
    readonly refundedMinor: string;
    readonly netMinor: string;
  }[];
  readonly daily: readonly {
    readonly date: string;
    readonly currency: string;
    readonly netMinor: string;
  }[];
  readonly funnel: {
    readonly created: number;
    readonly paid: number;
    readonly started: number;
    readonly delivered: number;
  };
  readonly byState: Readonly<Record<string, number>>;
  readonly stopCauses: readonly { readonly cause: string; readonly count: number }[];
  readonly paymentOptions: readonly { readonly option: string; readonly count: number }[];
  readonly declinedPayments: number;
  /** Not windowed: money owed from last month is still owed today. */
  readonly queues: {
    readonly refundOwed: readonly {
      readonly currency: string;
      readonly orders: number;
      readonly minor: string;
    }[];
    readonly refundFailed: number;
    readonly captureRequired: number;
    readonly captureFailed: number;
  };
}

/** A permission an App can hold (`server/src/config/appPermissions.ts`). */
export type AppPermission = "identities.read" | "sessions.read";

/** The group an App acts for: it sees that group's subtree and nothing else. */
export interface AppHolder {
  readonly id: string;
  readonly name: string;
  readonly type: HolderType;
}

export type RateWindow = "minute" | "hour" | "day" | "month";

/** A permission, or `*` for every request the App makes. */
export type AppLimitScope = AppPermission | "*";

export interface AppLimit {
  readonly scope: AppLimitScope;
  readonly window: RateWindow;
  readonly max: number;
}

/** `GET /apps` — one row. Today's counts are the quota day (Asia/Phnom_Penh). */
export interface AppSummary {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly disabled: boolean;
  readonly disabledAt: string | null;
  readonly createdAt: string;
  /** Null: the app sees every holder. */
  readonly holder: AppHolder | null;
  readonly push: AppPush | null;
  readonly permissions: readonly AppPermission[];
  readonly activeKeys: number;
  readonly todayAllowed: number;
  readonly todayDenied: number;
}

export type AppKeyStatus = "active" | "revoked" | "expired";

export interface AppKey {
  readonly id: string;
  /** The public part of the token, `ocpp_<prefix>_…` — enough to tell keys apart. */
  readonly prefix: string;
  readonly label: string | null;
  readonly status: AppKeyStatus;
  readonly expiresAt: string | null;
  readonly lastUsedAt: string | null;
  readonly revokedAt: string | null;
  readonly createdBy: string;
  readonly createdAt: string;
}

export interface AppUsageDay {
  /** Local midnight in the quota zone, as an instant. */
  readonly day: string;
  readonly scope: AppPermission;
  readonly allowed: number;
  readonly denied: number;
}

/** `GET /apps/:id`. */
export interface AppDetail {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly disabled: boolean;
  readonly disabledAt: string | null;
  readonly createdBy: string;
  readonly createdAt: string;
  /** Null: the app sees every holder. */
  readonly holder: AppHolder | null;
  readonly permissions: readonly AppPermission[];
  /** Overrides only; a permission's defaults apply wherever none is set. */
  readonly limits: readonly AppLimit[];
  /** Newest first. */
  readonly keys: readonly AppKey[];
  /** The last 30 quota days, only days with traffic. */
  readonly usage: readonly AppUsageDay[];
  /** Where its closed sessions are pushed; null pushes nothing. */
  readonly push: AppPushDetail | null;
}

/** The token is never sent back: only that one is stored. */
export interface AppPush {
  readonly url: string;
  readonly tokenSet: boolean;
  readonly updatedAt: string | null;
  readonly updatedBy: string | null;
}

/** What an App has not been sent: sessions that closed before its push was set, and parked pushes. */
export interface AppPushBacklog {
  readonly unsent: number;
  readonly parked: number;
}

/** The detail's push, with what "Push all unpushed" would queue. */
export interface AppPushDetail extends AppPush {
  readonly backlog: AppPushBacklog;
}

/** `POST /apps/:id/keys` — the only response that carries the token. */
export interface IssuedAppKey {
  readonly key: AppKey;
  readonly token: string;
}

/** `GET /apps/catalog`. */
export interface AppCatalog {
  readonly permissions: readonly {
    readonly key: AppPermission;
    readonly label: string;
    readonly description: string;
    /** The operator permission needed to grant it. */
    readonly grantedBy: Permission;
    readonly defaults: Partial<Record<RateWindow, number>>;
  }[];
  readonly windows: readonly RateWindow[];
  readonly appWideScope: "*";
  readonly timeZone: string;
}

/**
 * `GET /payment-providers` — one merchant account
 * (server `api/routes/paymentProviders.ts`).
 *
 * Credentials never come back: only whether each is set, when, and by whom.
 */
export interface PaymentProviderCredential {
  readonly set: boolean;
  readonly updatedAt: string | null;
  readonly updatedBy: string | null;
}

export type PaymentProviderEnvironment = "sandbox" | "production";

export interface PaymentProvider {
  readonly id: string;
  readonly type: "payway";
  readonly label: string;
  readonly environment: PaymentProviderEnvironment;
  readonly merchantId: string;
  readonly isDefault: boolean;
  readonly apiKey: PaymentProviderCredential;
  readonly refundKey: PaymentProviderCredential;
  readonly depots: readonly { readonly id: string; readonly name: string }[];
  readonly orderCount: number;
  readonly createdAt: string;
}
